import { Navigate, Route, Routes } from 'react-router-dom'

import { Permissions } from './api/requests'
import { RequireRoutePermission } from './components'
import { useSessionContext } from './contexts'
import { ActiveSubstances } from './features/active-substances'
import { Activity } from './features/activity'
import { AppointmentsPage } from './features/appointments'
import {
  ConfirmEmail,
  ForgotPassword,
  Login,
  OtpConfirmationPage,
  ResetConfirm,
  ResetPassword
} from './features/auth'
import { ChangeEmail } from './features/auth/pages/ChangeEmail'
import { CasesDetailsPage, CasesPage } from './features/cases'
import {
  ClassifiersDetailsPage,
  ClassifiersPage,
  Icd10OpportunisticPage
} from './features/classifiers'
import { InstitutionConsultationsPage } from './features/consultations'
import {
  AddContract,
  CounterParts,
  CounterPartsAdd,
  CounterPartsDetailsPage
} from './features/counterParts'
import { AddTemplateContract } from './features/counterParts/pages/AddTemplateContract'
import { CUATMClassifierListPage } from './features/cuatm'
import { DeviceTypesPage } from './features/device-types'
import { EquipmentDetailsPage, EquipmentsPage } from './features/equipments'
import { FundingSource } from './features/funding-source'
import { HivAnamnesisPage } from './features/hiv-anamnesis'
import { InstitutionEpidemiologyPage } from './features/institution-epidemiology'
import { InstitutionPregnancyBirths } from './features/institution-pregnancy-births'
import { InstitutionPregnancyNewBornsInvestigations } from './features/institution-pregnancy-investigation-newBorns'
import { InstitutionPregnancyInvestigations } from './features/institution-pregnancy-investigations'
import { InstitutionPregnancyMonitoring } from './features/institution-pregnancy-monitoring'
import { InstitutionPregnancyNewBorns } from './features/institution-pregnancy-newBorns'
import { InstitutionRecipesPage } from './features/institution-recipes'
import { InstitutionTreatmentsPage } from './features/institution-treatments'
import { InvestigationTemplates } from './features/investigation-templates'
import { InstitutionInvestigationPage } from './features/investigations'
import {
  InvestigationTypesPage,
  LaboratoryClassifiersPage,
  ReferenceIntervalsPage,
  TestTypesPage,
  ValuesOptionsPage
} from './features/laboratory-classifiers'
import { LaboratoryInvestigationsPage } from './features/laboratory-investigations'
import {
  ConsumablesPage,
  CreateConsumablePage,
  CreateProductPage,
  EditConsumablePage,
  EditProductPage,
  InternationalNamesPage,
  MedicinesRegisterPage,
  PharmaceuticalFormsPage,
  ProducersPage,
  ProductsPage,
  UnitsPage
} from './features/medicines-register'
import {
  NotificationTemplatesCreatePage,
  NotificationTemplatesEditPage,
  NotificationTemplatesPage
} from './features/notification-templates'
import { Notifications } from './features/notifications'
import {
  OrganizationalDetails,
  OrganizationalStructure
} from './features/organizations'
import {
  OtherInstitutionAddContract,
  OtherInstitutionsAddPage,
  OtherInstitutionsDetailsPage,
  OtherInstitutionsPage
} from './features/other-institutions'
import {
  AddSidaNotificationPage,
  PatientDetailsPage,
  PatientDiagnosesPage,
  PatientsListPage,
  SidaNotificationDetailsPage
} from './features/patients'
import {
  AddEpidemiologyCasePage,
  PersonEpidemiologyCasesPage,
  PersonInvestigationResultsPage,
  PersonInvestigationsPage,
  PersonPregnancyPage,
  PersonRecipesDetailsPage,
  PersonRecipesPage,
  PersonsDetailsPage,
  PersonsFromScreeningPage,
  PersonsHistoryPage,
  PersonsPage,
  PersonsRiskGroupsPage,
  PersonsScreeningPage,
  PersonTransfersPage,
  PersonTreatmentPage,
  PersonTreatmentsDetailsPage,
  QuizPage
} from './features/persons'
import {
  PersonalDetails,
  SimeTBDetailsDetails,
  SimeTBDetailsList
} from './features/persons/components'
import {
  AddConsultationPage,
  ConsultationDetailsPage,
  EditConsultationPage,
  PersonConsultations
} from './features/persons/pages/consultations'
import { PersonPregnancyTabsPage } from './features/persons/pages/pregnancy'
import { PersonPregnancyMonitoringPage } from './features/persons/pages/pregnancy-monitoring/PersonPregnancyMonitoringPage'
import { QuizDetails } from './features/persons/pages/quiz'
import {
  PrescriptionDetailsPage,
  PrescriptionsListPage
} from './features/prescriptions'
import {
  CreateQuestionnaires,
  EditQuestionnaires,
  ListQuestionnaires,
  PatientQuestionnaireAnswer,
  PatientQuestionnaireDetails
} from './features/questionnaires/pages'
import { ReportDetailsPage, ReportsPage } from './features/reports'
import {
  AnnuallyReportPage,
  ReportsAdministrationPage
} from './features/reports-administration'
import {
  PersonDetailsPage,
  RiskGroupsCandidatPage,
  RiskGroupsClassifierPage,
  RiskGroupsPage
} from './features/riskGroups'
import { Roles } from './features/roles'
import { ScreeningTbPage } from './features/screening-tb'
import { SettingsPage } from './features/settings'
import { SystemSettings } from './features/settings/pages'
import {
  IssuancesPage,
  StockApplicationsCreatePage,
  StockApplicationsDetails,
  StockApplicationsPage,
  StockInputsCreatePage,
  StockInputsDetailsPage,
  StockInputsPage,
  StockPage,
  StockReturnsCreatePage,
  StockReturnsDetailsPage,
  StockReturnsPage
} from './features/stock'
import {
  StockSettlementsCreatePage,
  StockSettlementsDetailsPage,
  StockSettlementsPage
} from './features/stock/pages/settlements'
import { StockPageMedicines } from './features/stock/pages/StockPageMedicines'
import { StockPagePharmaceutics } from './features/stock/pages/StockPagePharmaceutics'
import {
  StockAdminIssuanceApplicationsCreate,
  StockAdminIssuanceApplicationsDetails,
  StockIssuanceDetails
} from './features/stock-administration'
import { StockGroups } from './features/stock-groups'
import {
  LocalIssuancesPage,
  LocalStockInputsDetailsPage,
  LocalStockInputsPage,
  LocalStockIssuanceDetails,
  StockLocalApplicationsCreatePage,
  StockLocalApplicationsDetails,
  StockLocalApplicationsPage,
  StockLocalPage
} from './features/stock-local'
import { StockLocalIssuanceApplicationsDetails } from './features/stock-local/pages/applications'
import {
  LocalStockReturnsCreatePage,
  LocalStockReturnsDetailsPage,
  LocalStockReturnsPage
} from './features/stock-local/pages/returns'
import { SupportInvestigation } from './features/support-investigation'
import { TreatmentTemplatesPage } from './features/templates'
import {
  AddTestPage,
  SpecimenDetailsPage,
  TestDetailsPage,
  TestsListPage
} from './features/tests'
import { PatientTestResults, TestsResultsPage } from './features/tests-results'
import { TransfersPage } from './features/transfers'
import { TreatmentTypesClassifier } from './features/treatment-types-classifier'
import {
  TreatmentDetails,
  Treatments
} from './features/user-personal-treatments'
import { UsersCreatePage, UsersEditPage, UsersPage } from './features/users'
import { AuthenticationLayout, DashboardLayout } from './layouts'
import { paths } from './paths'

export const Router = () => {
  const { session } = useSessionContext()
  const isAuth = !!(session?.accessToken && session?.refreshToken)

  return (
    <Routes>
      <Route
        path={paths.auth.root}
        element={
          isAuth ? <Navigate to={paths.root} /> : <AuthenticationLayout />
        }
      >
        <Route index element={<Login />} />
        <Route path={paths.auth.confirmEmail} element={<ConfirmEmail />} />
        <Route path={paths.auth.confirm} element={<OtpConfirmationPage />} />
        <Route path={paths.auth.forgotPassword} element={<ForgotPassword />} />
        <Route path={paths.auth.resetConfirm} element={<ResetConfirm />} />
        <Route path={paths.auth.resetPassword} element={<ResetPassword />} />
      </Route>
      <Route
        path={paths.root}
        element={
          isAuth ? <DashboardLayout /> : <Navigate to={paths.auth.root} />
        }
      >
        <Route index element="Home" />

        {/* Organizational Structure */}
        <Route
          path={paths.organizationalStructure.root}
          element={
            <RequireRoutePermission
              requiredPermission={[
                Permissions.ORGANIZATIONAL_STRUCTURE_MENU_ACCESS_ORGANIZATIONAL_STRUCTURE,
                Permissions.ORGANIZATIONAL_STRUCTURE_ORGANIZATIONAL_STRUCTURE_VIEW_ALL,
                Permissions.ORGANIZATIONAL_STRUCTURE_ORGANIZATIONAL_STRUCTURE_VIEW_INSTITUTION
              ]}
            />
          }
        >
          <Route index element={<OrganizationalStructure />} />
          <Route
            path={paths.organizationalStructure.details(':id')}
            element={<OrganizationalDetails />}
          />
        </Route>

        {/* Institution */}
        <Route
          path={paths.institutions.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.COUNTERPART_MENU_ACCESS_OTHER_INSTITUTIONS
              }
            />
          }
        >
          <Route index element={<OtherInstitutionsPage />} />
          <Route
            path={paths.institutions.details(':id')}
            element={<OtherInstitutionsDetailsPage />}
          />
          <Route
            path={paths.institutions.edit(':id')}
            element={<OtherInstitutionsAddPage />}
          />
          <Route
            path={paths.institutions.add}
            element={<OtherInstitutionsAddPage />}
          />
          <Route
            path={paths.institutions.contratAdd(':id')}
            element={<OtherInstitutionAddContract />}
          />
          <Route
            path={paths.institutions.contractEdit(':id', ':contractId')}
            element={<OtherInstitutionAddContract />}
          />

          {/* contract templates */}
          <Route
            path={paths.institutions.templateContractAdd(':id')}
            element={<AddTemplateContract isCounterpart={false} />}
          />
          <Route
            path={paths.institutions.templateContractEdit(':id', ':contractId')}
            element={<AddTemplateContract isCounterpart={false} />}
          />
        </Route>

        {/* Users  */}
        <Route
          path={paths.users.root}
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.IDENTITY_MENU_ACCESS_USERS}
            />
          }
        >
          <Route index element={<UsersPage />} />
          <Route path={paths.users.edit(':id')} element={<UsersEditPage />} />
        </Route>

        <Route
          path={paths.users.root}
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.IDENTITY_MENU_ACCESS_USERS}
            />
          }
        >
          <Route path={paths.users.create} element={<UsersCreatePage />} />
        </Route>

        {/* Patients */}
        <Route
          path={paths.patients.root}
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.PERSONS_MENU_ACCESS_PATIENTS}
            />
          }
        >
          <Route index element={<PatientsListPage />} />

          <Route
            path={paths.patients.details(':id')}
            element={<PatientDetailsPage />}
          >
            <Route index element={<PersonalDetails />} />
            <Route
              path={paths.patients.consultations.root()}
              element={<PersonConsultations />}
            />
            <Route
              path={paths.patients.history.root()}
              element={<PersonsHistoryPage />}
            />
            <Route
              path={paths.patients.riskGroups.root()}
              element={<PersonsRiskGroupsPage />}
            />

            <Route
              path={paths.patients.investigations.root()}
              element={<PersonInvestigationsPage />}
            />
            <Route
              path={paths.patients.investigationResults.root()}
              element={<PersonInvestigationResultsPage />}
            />
            <Route
              path={paths.patients.treatments.root()}
              element={<PersonTreatmentPage />}
            />
            <Route
              path={paths.patients.recipes.root()}
              element={<PersonRecipesPage />}
            />
            <Route path={paths.patients.quiz.root()} element={<QuizPage />} />
            <Route
              path={paths.patients.pregnancy.root()}
              element={<PersonPregnancyPage />}
            />
            <Route
              path={paths.patients.epidemiologyCases.root()}
              element={<PersonEpidemiologyCasesPage />}
            />
            <Route
              path={paths.patients.transfers.root()}
              element={<PersonTransfersPage />}
            />
            <Route
              path={paths.patients.diagnoses.root()}
              element={<PatientDiagnosesPage />}
            />
          </Route>
        </Route>

        <Route
          path={paths.patients.consultations.new(':personId')}
          element={<AddConsultationPage />}
        />

        <Route
          path={paths.patients.consultations.draft(':personId', ':draftId')}
          element={<AddConsultationPage />}
        />

        <Route
          path={paths.patients.consultations.details(':personId', ':id')}
          element={<ConsultationDetailsPage />}
        />

        <Route
          path={paths.patients.consultations.edit(':personId', ':id')}
          element={<EditConsultationPage />}
        />

        <Route
          path={paths.patients.treatments.details(':personId', ':id')}
          element={<PersonTreatmentsDetailsPage />}
        />

        <Route
          path={paths.patients.epidemiologyCases.add(':personId')}
          element={<AddEpidemiologyCasePage />}
        />

        <Route
          path={paths.patients.epidemiologyCases.add(
            ':personId',
            ':consultationId'
          )}
          element={<AddEpidemiologyCasePage />}
        />

        <Route
          path={paths.patients.sidaNotification.add(':personId')}
          element={<AddSidaNotificationPage />}
        />

        <Route
          path={paths.patients.sidaNotification.details(':personId')}
          element={<SidaNotificationDetailsPage />}
        />

        <Route
          path={paths.patients.hivAnamnesis.root(':personId')}
          element={<HivAnamnesisPage />}
        />

        <Route
          path={paths.patients.epidemiologyCases.details(':personId', ':id')}
          element={<CasesDetailsPage />}
        />

        <Route
          path={paths.patients.recipes.details(
            ':personId',
            ':treatmentPlanId',
            ':id'
          )}
          element={<PersonRecipesDetailsPage />}
        />

        <Route
          path={paths.patients.pregnancy.details(':personId', ':id')}
          element={<PersonPregnancyTabsPage />}
        />

        <Route
          path={paths.patients.quiz.details(':personId', ':id')}
          element={<QuizDetails />}
        />

        {/* person quiz  */}
        <Route
          path={paths.patients.quiz.quizDetails(':id')}
          element={<PatientQuestionnaireDetails />}
        />

        <Route
          path={paths.patients.quiz.complete(':id')}
          element={<PatientQuestionnaireAnswer />}
        />

        <Route
          path={paths.patients.simeTB.root(':id', ':simeTBPersonId')}
          element={<SimeTBDetailsList />}
        />

        <Route
          path={paths.patients.simeTB.details(
            ':personId',
            ':simeTBPersonId',
            ':id'
          )}
          element={<SimeTBDetailsDetails />}
        />

        {/* Persons  */}
        <Route
          path={paths.persons.root}
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.PERSONS_MENU_ACCESS_PERSONS}
            />
          }
        >
          <Route index element={<PersonsPage />} />

          <Route
            path={paths.persons.details(':id')}
            element={<PersonsDetailsPage />}
          >
            <Route index element={<PersonalDetails />} />
            <Route
              path={paths.persons.consultations.root()}
              element={<PersonConsultations />}
            />
            <Route
              path={paths.persons.history.root()}
              element={<PersonsHistoryPage />}
            />
            <Route
              path={paths.persons.riskGroups.root()}
              element={<PersonsRiskGroupsPage />}
            />
            <Route
              path={paths.persons.investigations.root()}
              element={<PersonInvestigationsPage />}
            />
            <Route
              path={paths.persons.investigationResults.root()}
              element={<PersonInvestigationResultsPage />}
            />
            <Route
              path={paths.persons.screening.root()}
              element={<PersonsScreeningPage />}
            />
          </Route>
        </Route>

        {/* Persons from screening */}
        <Route
          path={paths.personsFromScreening}
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.PERSONS_MENU_ACCESS_PERSONS}
            />
          }
        >
          <Route index element={<PersonsFromScreeningPage />} />
        </Route>

        <Route
          path={paths.persons.consultations.new(':personId')}
          element={<AddConsultationPage />}
        />

        <Route
          path={paths.persons.consultations.draft(':personId', ':draftId')}
          element={<AddConsultationPage />}
        />

        <Route
          path={paths.persons.consultations.details(':personId', ':id')}
          element={<ConsultationDetailsPage />}
        />

        <Route
          path={paths.persons.consultations.edit(':personId', ':id')}
          element={<EditConsultationPage />}
        />

        {/* Stocks  */}
        <Route
          element={
            <RequireRoutePermission
              requiredPermission={[
                Permissions.STOCKS_MENU_ACCESS_STOCKS,
                Permissions.STOCKS_MENU_ACCESS_STOCKS_ENTRIES,
                Permissions.STOCKS_MENU_ACCESS_STOCKS_REQUESTS,
                Permissions.STOCKS_MENU_ACCESS_STOCKS_RETURN,
                Permissions.STOCKS_MENU_ACCESS_STOCKS_WRITE_OFF,
                Permissions.STOCKS_MENU_ACCESS_ISSUANCE
              ]}
            />
          }
        >
          <Route
            path={paths.stock.stock}
            element={
              <RequireRoutePermission
                requiredPermission={Permissions.STOCKS_MENU_ACCESS_STOCKS}
              />
            }
          >
            <Route index element={<StockPage />} />
          </Route>

          <Route
            path={paths.stock.stockGroupsMedicine}
            element={
              <RequireRoutePermission
                requiredPermission={Permissions.STOCKS_MENU_ACCESS_STOCKS}
              />
            }
          >
            <Route index element={<StockPageMedicines />} />
          </Route>

          <Route
            path={paths.stock.stockGroupsPharmaceutics}
            element={
              <RequireRoutePermission
                requiredPermission={Permissions.STOCKS_MENU_ACCESS_STOCKS}
              />
            }
          >
            <Route index element={<StockPagePharmaceutics />} />
          </Route>

          <Route
            path={paths.stock.applications}
            element={
              <RequireRoutePermission
                requiredPermission={[
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_REQUESTS,
                  Permissions.STOCKS_MENU_ACCESS_ISSUANCE
                ]}
              />
            }
          >
            <Route index element={<StockApplicationsPage />} />
          </Route>

          {/*  Stock Institutions */}
          <Route
            path={paths.stock.applicationCreate}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_REQUESTS
                }
              />
            }
          >
            <Route index element={<StockApplicationsCreatePage />} />
          </Route>
          <Route
            path={paths.stock.applicationEdit(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_REQUESTS
                }
              />
            }
          >
            <Route index element={<StockApplicationsCreatePage />} />
          </Route>

          <Route
            path={paths.stock.applicationDetails(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_REQUESTS
                }
              />
            }
          >
            <Route index element={<StockApplicationsDetails />} />
          </Route>

          {/*  Stock National Applications */}

          <Route
            path={paths.stockAdmin.issuanceCreate}
            element={
              <RequireRoutePermission
                requiredPermission={Permissions.STOCKS_MENU_ACCESS_ISSUANCE}
              />
            }
          >
            <Route index element={<StockAdminIssuanceApplicationsCreate />} />
          </Route>

          <Route
            path={paths.stockAdmin.issuanceDetails(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={Permissions.STOCKS_MENU_ACCESS_ISSUANCE}
              />
            }
          >
            <Route index element={<StockAdminIssuanceApplicationsDetails />} />
          </Route>

          {/*  Stock Issuance */}
          <Route
            path={paths.issuance.root}
            element={
              <RequireRoutePermission
                requiredPermission={Permissions.STOCKS_MENU_ACCESS_ISSUANCE}
              />
            }
          >
            <Route index element={<IssuancesPage />} />
          </Route>

          <Route
            path={paths.issuance.issuanceDetails(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={Permissions.STOCKS_MENU_ACCESS_ISSUANCE}
              />
            }
          >
            <Route index element={<StockIssuanceDetails />} />
          </Route>

          {/*  Stock Inputs */}
          <Route
            path={paths.inputs.root}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_ENTRIES
                }
              />
            }
          >
            <Route index element={<StockInputsPage />} />
          </Route>

          <Route
            path={paths.inputs.create}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_ENTRIES
                }
              />
            }
          >
            <Route index element={<StockInputsCreatePage />} />
          </Route>

          <Route
            path={paths.inputs.details(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_ENTRIES
                }
              />
            }
          >
            <Route index element={<StockInputsDetailsPage />} />
          </Route>

          {/* Stock returns */}
          <Route
            path={paths.stock.returns.root}
            element={
              <RequireRoutePermission
                requiredPermission={[
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_RETURN,
                  Permissions.STOCKS_STOCK_CREATE_RETURNS
                ]}
              />
            }
          >
            <Route index element={<StockReturnsPage />} />

            <Route
              path={paths.stock.returns.details(':id')}
              element={<StockReturnsDetailsPage />}
            />

            <Route
              path={paths.stock.returns.create}
              element={<StockReturnsCreatePage />}
            />
          </Route>

          {/*  Stock Write Off */}

          <Route
            path={paths.settlements.root}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_WRITE_OFF
                }
              />
            }
          >
            <Route index element={<StockSettlementsPage />} />
          </Route>
          <Route
            path={paths.settlements.create}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_WRITE_OFF
                }
              />
            }
          >
            <Route index element={<StockSettlementsCreatePage />} />
          </Route>

          <Route
            path={paths.settlements.edit(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_WRITE_OFF
                }
              />
            }
          >
            <Route index element={<StockSettlementsCreatePage />} />
          </Route>

          <Route
            path={paths.settlements.details(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_MENU_ACCESS_STOCKS_WRITE_OFF
                }
              />
            }
          >
            <Route index element={<StockSettlementsDetailsPage />} />
          </Route>

          <Route
            path={paths.stock.stockGroups}
            element={
              <RequireRoutePermission
                requiredPermission={Permissions.STOCKS_MENU_ACCESS_ISSUANCE}
              />
            }
          >
            <Route index element={<StockGroups />} />
          </Route>

          <Route
            path={paths.activeSubstances}
            element={
              <RequireRoutePermission
                requiredPermission={Permissions.STOCKS_MENU_ACCESS_ISSUANCE}
              />
            }
          >
            <Route index element={<ActiveSubstances />} />
          </Route>
        </Route>

        {/* Reports  */}
        <Route
          path={paths.reports.root}
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.REPORTS_MENU_ACCESS_REPORTS}
            />
          }
        >
          <Route index element={<ReportsPage />} />
          <Route
            path={paths.reports.details(':id')}
            element={<ReportDetailsPage />}
          />
        </Route>

        {/* Classifiers  */}
        <Route
          path={paths.classifiers.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
              }
            />
          }
        >
          <Route index element={<ClassifiersPage />} />
          <Route
            path={paths.classifiers.details(':id')}
            element={<ClassifiersDetailsPage />}
          />
          <Route
            path={paths.classifiers.opportunisticDiseases}
            element={<Icd10OpportunisticPage />}
          />
        </Route>

        <Route
          path={paths.treatmentTypesClassifier.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
              }
            />
          }
        >
          <Route index element={<TreatmentTypesClassifier />} />
        </Route>

        {/* Investigation templates */}

        <Route
          path={paths.tests.templates}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
              }
            />
          }
        >
          <Route index element={<InvestigationTemplates />} />
        </Route>

        <Route
          path={paths.deviceTypes}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
              }
            />
          }
        >
          <Route index element={<DeviceTypesPage />} />
        </Route>

        {/* Notification Templates  */}
        <Route
          path={paths.notificationTemplates.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.NOTIFICATIONS_MENU_ACCESS_NOTIFICATION_TEMPLATES
              }
            />
          }
        >
          <Route index element={<NotificationTemplatesPage />} />
          <Route
            path={paths.notificationTemplates.create}
            element={<NotificationTemplatesCreatePage />}
          />
          <Route
            path={paths.notificationTemplates.edit(':id')}
            element={<NotificationTemplatesEditPage />}
          />
        </Route>

        {/* Templates */}
        <Route
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.TEMPLATES_MENU_VIEW_TEMPLATES}
            />
          }
        >
          <Route
            path={paths.templates.treatments}
            element={<TreatmentTemplatesPage />}
          />
        </Route>

        {/* notifications  */}
        <Route
          path={paths.notifications}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.NOTIFICATIONS_MENU_ACCESS_NOTIFICATIONS
              }
            />
          }
        >
          <Route index element={<Notifications />} />
        </Route>

        {/* Roles  */}
        <Route
          path={paths.roles}
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.IDENTITY_MENU_ACCESS_ROLES}
            />
          }
        >
          <Route index element={<Roles />} />
        </Route>

        {/* Activity  */}
        <Route
          path={paths.logs}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.NOTIFICATIONS_MENU_ACCESS_ACTIVITIES
              }
            />
          }
        >
          <Route index element={<Activity />} />
        </Route>

        {/* Settings  */}
        <Route
          path={paths.settings}
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.IDENTITY_MENU_ACCESS_SETTINGS}
            />
          }
        >
          <Route index element={<SettingsPage />} />
        </Route>

        {/* System Settings  */}
        <Route
          path={paths.systemSettings}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.SETTINGS_MENU_ACCESS_SYSTEM_CONFIGURATIONS
              }
            />
          }
        >
          <Route index element={<SystemSettings />} />
        </Route>

        {/* PERSON Appointments  */}
        <Route
          path={paths.appointments}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_PERSONAL_SCHEDULING
              }
            />
          }
        >
          <Route index element={<AppointmentsPage />} />
        </Route>

        {/* PERSON Treatments  */}
        <Route
          path={paths.treatments.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_PERSONAL_TREATMENTS
              }
            />
          }
        >
          <Route index element={<Treatments />} />
          <Route
            path={paths.treatments.details(':id')}
            element={<TreatmentDetails />}
          />
        </Route>

        {/* PERSON Test Results  */}
        <Route
          path={paths.personTestResults}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.INVESTIGATIONS_MENU_ACCESS_PERSONAL_TEST_RESULTS
              }
            />
          }
        >
          <Route index element={<PatientTestResults />} />
        </Route>

        {/* PERSON Pregnancy Monitoring  */}
        <Route
          path={paths.pregnancyMonitoring}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_PERSONAL_PREGNANCY_MONITORING
              }
            />
          }
        >
          <Route index element={<PersonPregnancyMonitoringPage />} />
        </Route>

        {/* Test Results  */}
        <Route
          path={paths.testsResults}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.INVESTIGATIONS_MENU_ACCESS_TEST_RESULTS
              }
            />
          }
        >
          <Route index element={<TestsResultsPage />} />
        </Route>

        {/* Cases */}
        <Route
          path={paths.epidemiologyCases.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_EPIDEMIOLOGY_CASE
              }
            />
          }
        >
          <Route index element={<CasesPage />} />
          <Route
            path={paths.epidemiologyCases.view(':id')}
            element={<CasesDetailsPage />}
          />
        </Route>

        {/* Transfers */}
        <Route
          path={paths.transfers}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.PERSONS_MENU_ACCESS_PERSON_TRANSFER_REQUEST
              }
            />
          }
        >
          <Route index element={<TransfersPage />} />
        </Route>

        {/*  Administative Reports */}
        <Route
          path={paths.reportsAdministration.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.REPORTS_MENU_ACCESS_REPORTS_MANAGEMENT
              }
            />
          }
        >
          <Route index element={<ReportsAdministrationPage />} />
          <Route
            path={paths.reportsAdministration.view(':id')}
            element={<AnnuallyReportPage />}
          />
        </Route>

        {/* Support Investigations */}
        <Route
          path={paths.supportInvestigation}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.INVESTIGATIONS_MENU_ACCESS_INVESTIGATIONS_SUPPORT
              }
            />
          }
        >
          <Route index element={<SupportInvestigation />} />
        </Route>

        {/* Contraparte */}
        <Route
          path={paths.counterpart.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.COUNTERPART_MENU_ACCESS_COUNTERPARTS
              }
            />
          }
        >
          <Route index element={<CounterParts />} />
          <Route
            path={paths.counterpart.details(':id')}
            element={<CounterPartsDetailsPage />}
          />
          <Route
            path={paths.counterpart.edit(':id')}
            element={<CounterPartsAdd />}
          />
          <Route path={paths.counterpart.add} element={<CounterPartsAdd />} />
          <Route
            path={paths.counterpart.contratAdd(':id')}
            element={<AddContract />}
          />
          <Route
            path={paths.counterpart.contractEdit(':id', ':contractId')}
            element={<AddContract />}
          />
          {/* contract template */}
          <Route
            path={paths.counterpart.templateContractAdd(':id')}
            element={<AddTemplateContract isCounterpart />}
          />
          <Route
            path={paths.counterpart.templateContractEdit(':id', ':contractId')}
            element={<AddTemplateContract isCounterpart />}
          />
        </Route>

        {/* Medicines Register */}
        <Route
          path={paths.medicinesRegister.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS_CONSUMABLE_MEDICINES
              }
            />
          }
        >
          <Route index element={<MedicinesRegisterPage />} />

          <Route
            path={paths.medicinesRegister.pharmaceuticalForms}
            element={<PharmaceuticalFormsPage />}
          />

          <Route
            path={paths.medicinesRegister.internationalNames}
            element={<InternationalNamesPage />}
          />

          <Route
            path={paths.medicinesRegister.producers}
            element={<ProducersPage />}
          />

          <Route path={paths.medicinesRegister.units} element={<UnitsPage />} />

          <Route path={paths.medicinesRegister.products.root}>
            <Route index element={<ProductsPage />} />

            <Route
              path={paths.medicinesRegister.products.add}
              element={<CreateProductPage />}
            />

            <Route
              path={paths.medicinesRegister.products.edit(':id')}
              element={<EditProductPage />}
            />
          </Route>

          <Route path={paths.medicinesRegister.consumables.root}>
            <Route index element={<ConsumablesPage />} />

            <Route
              path={paths.medicinesRegister.consumables.add}
              element={<CreateConsumablePage />}
            />

            <Route
              path={paths.medicinesRegister.consumables.edit(':id')}
              element={<EditConsumablePage />}
            />
          </Route>
        </Route>

        {/* Risk Groups */}

        <Route
          path={paths.riskGroups.root}
          element={
            <RequireRoutePermission
              requiredPermission={[
                Permissions.PRE_SCREENING_MENU_ACCESS_RISK_GROUPS
              ]}
            />
          }
        >
          <Route index element={<RiskGroupsPage />} />
          <Route
            path={paths.riskGroups.details(':id')}
            element={<RiskGroupsCandidatPage />}
          />
          <Route
            path={paths.riskGroups.personDetails(':riskId', ':id')}
            element={<PersonDetailsPage />}
          />
        </Route>

        {/* STOCK LOCAL */}

        <Route
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL
              }
            />
          }
        >
          <Route
            path={paths.stockLocal.stock}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL
                }
              />
            }
          >
            <Route index element={<StockLocalPage />} />
          </Route>

          <Route
            path={paths.stockLocal.applications}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_REQUESTS
                }
              />
            }
          >
            <Route index element={<StockLocalApplicationsPage />} />
          </Route>

          <Route
            path={paths.stockLocal.applicationCreate}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_REQUESTS
                }
              />
            }
          >
            <Route index element={<StockLocalApplicationsCreatePage />} />
          </Route>

          <Route
            path={paths.stockLocal.applicationEdit(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_REQUESTS
                }
              />
            }
          >
            <Route index element={<StockLocalApplicationsCreatePage />} />
          </Route>

          <Route
            path={paths.stockLocal.applicationDetails(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_REQUESTS
                }
              />
            }
          >
            <Route index element={<StockLocalApplicationsDetails />} />
          </Route>

          {/*   Stock LOCAL INSTITUTION Applications */}

          <Route
            path={paths.stockLocal.stockAdmin.issuanceDetails(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_ISSUANCE
                }
              />
            }
          >
            <Route index element={<StockLocalIssuanceApplicationsDetails />} />
          </Route>

          {/*  Stock LOCAL Issuance */}
          <Route
            path={paths.stockLocal.issuance.root}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_ISSUANCE
                }
              />
            }
          >
            <Route index element={<LocalIssuancesPage />} />
          </Route>

          <Route
            path={paths.stockLocal.issuance.issuanceDetails(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_ISSUANCE
                }
              />
            }
          >
            <Route index element={<LocalStockIssuanceDetails />} />
          </Route>

          {/*  Stock LOCAL Inputs */}
          <Route
            path={paths.stockLocal.inputs.root}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_ENTRIES
                }
              />
            }
          >
            <Route index element={<LocalStockInputsPage />} />
          </Route>

          <Route
            path={paths.stockLocal.inputs.details(':id')}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_ENTRIES
                }
              />
            }
          >
            <Route index element={<LocalStockInputsDetailsPage />} />
          </Route>

          {/* Stock LOCAL returns */}
          <Route
            path={paths.stockLocal.returns.root}
            element={
              <RequireRoutePermission
                requiredPermission={
                  Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_RETURN
                }
              />
            }
          >
            <Route index element={<LocalStockReturnsPage />} />

            <Route
              path={paths.stockLocal.returns.details(':id')}
              element={<LocalStockReturnsDetailsPage />}
            />

            <Route
              path={paths.stockLocal.returns.create}
              element={<LocalStockReturnsCreatePage />}
            />
          </Route>
        </Route>

        {/* Tests */}

        <Route
          path={paths.tests.root}
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.INVESTIGATIONS_MENU_ACCESS_TESTS}
            />
          }
        >
          <Route index element={<TestsListPage />} />
          <Route path={paths.tests.add} element={<AddTestPage />} />
          <Route
            path={paths.tests.specimenDetails(':id')}
            element={<SpecimenDetailsPage />}
          />

          <Route
            path={paths.tests.testDetails(':investigationId', ':id')}
            element={<TestDetailsPage />}
          />
        </Route>

        {/* Questionnaires */}

        <Route
          path={paths.questionnaires.root}
          element={
            <RequireRoutePermission
              requiredPermission={[
                Permissions.QUESTIONNAIRES_MENU_ACCESS_PATIENT_QUESTIONNAIRES,
                Permissions.QUESTIONNAIRES_MENU_ACCESS_PERSONAL_QUESTIONNAIRES,
                Permissions.QUESTIONNAIRES_MENU_ACCESS_QUESTIONNAIRES
              ]}
            />
          }
        >
          <Route index element={<ListQuestionnaires />} />
          <Route
            path={paths.questionnaires.create}
            element={<CreateQuestionnaires />}
          />
          <Route
            path={paths.questionnaires.edit(':id')}
            element={<EditQuestionnaires />}
          />
        </Route>

        {/* Institution Consultations */}
        <Route
          path={paths.institutionConsultations}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_CONSULTATIONS
              }
            />
          }
        >
          <Route index element={<InstitutionConsultationsPage />} />
        </Route>

        {/* Institution Investigations */}
        <Route
          path={paths.institutionInvestigations}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.INVESTIGATIONS_MENU_ACCESS_INVESTIGATIONS
              }
            />
          }
        >
          <Route index element={<InstitutionInvestigationPage />} />
        </Route>

        {/* Screening TB */}
        <Route
          path={paths.screeningTb}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.INVESTIGATIONS_MENU_SCREENING_TB_TAB
              }
            />
          }
        >
          <Route index element={<ScreeningTbPage />} />
        </Route>

        {/* Institution Epidemiology Cases */}
        <Route
          path={paths.institutionEpidemiologyCases}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_EPIDEMIOLOGY_CASE
              }
            />
          }
        >
          <Route index element={<InstitutionEpidemiologyPage />} />
        </Route>

        {/* Institution Treatments */}
        <Route
          path={paths.institutionTreatments}
          element={
            <RequireRoutePermission
              requiredPermission={Permissions.TREATMENT_MENU_ACCESS_TREATMENTS}
            />
          }
        >
          <Route index element={<InstitutionTreatmentsPage />} />
        </Route>

        {/* Institution Pregnancy Monitoring */}
        <Route
          path={paths.institutionPregnancy}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_PREGNANCY_MONITORING
              }
            />
          }
        >
          <Route index element={<InstitutionPregnancyMonitoring />} />
        </Route>

        {/* Institution Pregnancy Births */}
        <Route
          path={paths.institutionBirths}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_PREGNANCY_MONITORING_BIRTH_VIEW_BIRTH_S
              }
            />
          }
        >
          <Route index element={<InstitutionPregnancyBirths />} />
        </Route>

        {/* Institution Pregnancy Investigations */}
        <Route
          path={paths.institutionPregnancyInvestigations}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_PREGNANCY_MONITORING
              }
            />
          }
        >
          <Route index element={<InstitutionPregnancyInvestigations />} />
        </Route>

        {/* Institution Pregnancy NewBorns */}
        <Route
          path={paths.institutionNewborns}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_PREGNANCY_MONITORING_NEWBORN_VIEW_NEWBORN_S
              }
            />
          }
        >
          <Route index element={<InstitutionPregnancyNewBorns />} />
        </Route>

        {/* Institution Pregnancy Newborn investigations */}
        <Route
          path={paths.institutionPregnancyNewBornInvestigations}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_PREGNANCY_MONITORING
              }
            />
          }
        >
          <Route
            index
            element={<InstitutionPregnancyNewBornsInvestigations />}
          />
        </Route>

        {/* Institution Recipes */}
        <Route
          path={paths.institutionPrescriptions}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_PRESCRIPTIONS
              }
            />
          }
        >
          <Route index element={<InstitutionRecipesPage />} />
        </Route>

        {/* Funding source */}

        <Route
          path={paths.fundingSource}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
              }
            />
          }
        >
          <Route index element={<FundingSource />} />
        </Route>

        {/* Group risks classifier */}

        <Route
          path={paths.riskGroups.classifier}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
              }
            />
          }
        >
          <Route index element={<RiskGroupsClassifierPage />} />
        </Route>

        {/* CUATM Classifier */}

        <Route
          path={paths.cuatmClassifier.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
              }
            />
          }
        >
          <Route index element={<CUATMClassifierListPage />} />
        </Route>

        {/* Prescriptions */}
        <Route
          path={paths.prescriptions.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.TREATMENT_MENU_ACCESS_PRESCRIPTIONS
              }
            />
          }
        >
          <Route index element={<PrescriptionsListPage />} />
          <Route
            path={paths.prescriptions.details(':treatmentPlanId', ':id')}
            element={<PrescriptionDetailsPage />}
          />
        </Route>

        <Route
          path={paths.equipments.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.ORGANIZATIONAL_STRUCTURE_MENU_ACCESS_EQUIPMENTS
              }
            />
          }
        >
          <Route index element={<EquipmentsPage />} />
          <Route
            path={paths.equipments.details(':id')}
            element={<EquipmentDetailsPage />}
          />
        </Route>

        <Route
          path={paths.laboratoryClassifier.root}
          element={
            <RequireRoutePermission
              requiredPermission={
                Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
              }
            />
          }
        >
          <Route index element={<LaboratoryClassifiersPage />} />
          <Route
            path={paths.laboratoryClassifier.valueOptions}
            element={<ValuesOptionsPage />}
          />
          <Route
            path={paths.laboratoryClassifier.referenceIntervals}
            element={<ReferenceIntervalsPage />}
          />
          <Route
            path={paths.laboratoryClassifier.testTypes}
            element={<TestTypesPage />}
          />
          <Route
            path={paths.laboratoryClassifier.investigationTypes}
            element={<InvestigationTypesPage />}
          />
        </Route>

        {/* Investigations */}
        <Route
          path={paths.investigations}
          element={<LaboratoryInvestigationsPage />}
        />
      </Route>

      <Route path={paths.auth.root} element={<AuthenticationLayout />}>
        <Route path={paths.auth.changeEmail} element={<ChangeEmail />} />
      </Route>
    </Routes>
  )
}
